import React from "react";
import { usePersonalization } from "../hooks/usePersonalization";
import PaginationControls from "./PaginationControls";

function ExpenseList({
  expenses,
  settlements,
  onDeleteExpense,
  onDeleteSettlement,
  onEditExpense,
  loading,
  couple,
  currentUserId,
  currentPage,
  hasMoreItems,
  onPreviousPage,
  onNextPage,
  totalItems,
  showPagination = true,
}) {
  const p = usePersonalization(couple, currentUserId);
  if (loading) {
    return (
      <div className="expense-list">
        <h2>History</h2>
        <div className="loading-state">Loading expenses...</div>
      </div>
    );
  }

  // Combine expenses and settlements into a single timeline
  const combinedHistory = [
    ...expenses.map((exp) => ({
      ...exp,
      type: "expense",
      sortDate: new Date(exp.created_at),
    })),
    ...settlements.map((set) => ({
      ...set,
      type: "settlement",
      sortDate: new Date(set.settled_at),
    })),
  ].sort((a, b) => b.sortDate - a.sortDate);

  if (combinedHistory.length === 0) {
    return (
      <div className="expense-list">
        <h2>History</h2>
        <div className="empty-state">
          No expenses yet. Add your first one above!
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year:
        date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
    });
  };

  const formatAmount = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  const getOwedAmount = (expense) => {
    if (expense.split_mode === "FULL_REIMBURSEMENT") {
      return parseFloat(expense.amount);
    }
    if (expense.split_amount !== null) {
      return parseFloat(expense.split_amount);
    }
    return parseFloat(expense.amount) / 2;
  };

  const getSettlementLabel = (settlement) => {
    if (settlement.settled_by === currentUserId) {
      return "You paid";
    }
    return `${p.partnerName || "Partner"} paid`;
  };

  return (
    <div className="expense-list">
      <h2>History</h2>
      <div className="expense-items">
        {combinedHistory.map((item) =>
          item.type === "settlement" ? (
            <div
              key={`settlement-${item.id}`}
              className="expense-item settlement"
            >
              <div className="expense-main">
                <div className="expense-info">
                  <div className="expense-description">Settled up</div>
                  <div className="expense-meta">
                    <span className="expense-date">{formatDate(item.settled_at)}</span>
                    <span className="expense-payer">{getSettlementLabel(item)}</span>
                    {item.note && <span className="expense-payer">{item.note}</span>}
                  </div>
                </div>
                <div className="expense-amount-section">
                  <div className="expense-amount settled">
                    ${formatAmount(Math.abs(item.amount))}
                  </div>
                </div>
              </div>
              {onDeleteSettlement && (
                <button
                  className="delete-btn"
                  onClick={() => onDeleteSettlement(item.id)}
                  aria-label="Delete settlement"
                  title="Delete"
                >
                  ×
                </button>
              )}
            </div>
          ) : (
            <div key={`expense-${item.id}`} className="expense-item">
              <div className="expense-main">
                <div className="expense-info">
                  <div className="expense-description">{item.description}</div>
                  <div className="expense-meta">
                    <span className="expense-date">
                      {formatDate(item.date)}
                    </span>
                    {item.category && (
                      <span className="expense-payer">{item.category}</span>
                    )}
                    <span className="expense-payer">
                      {p.getPaidByLabel(item.paid_by)}
                    </span>
                  </div>
                </div>
                <div className="expense-amount-section">
                  <div className="expense-total">
                    ${formatAmount(item.amount)}
                  </div>
                  <div className="expense-owed">
                    {p.getOwesLabel(item.paid_by, getOwedAmount(item))}
                  </div>
                </div>
              </div>
              {onEditExpense && (
                <button
                  className="delete-btn"
                  onClick={() => onEditExpense(item)}
                  aria-label="Edit expense"
                  title="Edit"
                >
                  Edit
                </button>
              )}
              <button
                className="delete-btn"
                onClick={() => onDeleteExpense(item.id)}
                aria-label="Delete expense"
                title="Delete"
              >
                ×
              </button>
            </div>
          ),
        )}
      </div>

      {showPagination && (
        <PaginationControls
          currentPage={currentPage}
          hasMoreItems={hasMoreItems}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          disabled={loading}
          totalItems={totalItems}
          itemsPerPage={10}
        />
      )}
    </div>
  );
}

export default ExpenseList;
